export interface CampaignUpdate {
  id: string;
  title: string;
  body: string;
  date: string;
  time: string;
  image?: string;
  type: 'progress' | 'milestone' | 'news' | 'thanks';
}

export interface ExpertEndorsement {
  id: string;
  name: string;
  title: string;
  specialty: 'academic' | 'entrepreneur' | 'social' | 'religious';
  avatar: string;
  quote: string;
  date: string;
}

export interface ImpactMedia {
  type: 'image' | 'video';
  url: string;
  caption: string;
}

export interface ImpactExpense {
  label: string;
  amount: number;
  percentage: number;
  icon: string;
}

export interface ImpactReport {
  publishedAt: string;
  summary: string;
  thankYouMessage: string;
  expenses: ImpactExpense[];
  outcomes: string[];
  media: ImpactMedia[];
  beneficiariesCount: number;
  completionRate: number;
}

export interface Campaign {
  id: string;
  title: string;
  description: string;
  category: string;
  type: 'personal_support' | 'charity';
  goalAmount: number;
  raisedAmount: number;
  coverImage: string;
  creatorName: string;
  creatorAvatar: string;
  creatorUsername?: string;
  donorsCount: number;
  daysLeft: number;
  isVerified: boolean;
  isLicensed?: boolean;
  tags: string[];
  location: string;
  updates?: CampaignUpdate[];
  impactReport?: ImpactReport;
  endorsements?: ExpertEndorsement[];
}

export const campaigns: Campaign[] = [
  {
    id: '1',
    title: 'دعم مشروع تطبيق تعليمي للأطفال',
    description: 'نسعى لبناء تطبيق تعليمي تفاعلي يساعد أطفال المرحلة الابتدائية على تعلم الرياضيات والعلوم بطريقة ممتعة وشيقة، مع محتوى عربي أصيل يراعي القيم الإسلامية.',
    category: 'تعليم',
    type: 'personal_support',
    goalAmount: 50000,
    raisedAmount: 32500,
    coverImage: 'https://readdy.ai/api/search-image?query=Arabic%20children%20learning%20app%20colorful%20educational%20technology%20tablet%20classroom%20warm%20tones%20Saudi%20Arabia&width=800&height=500&seq=1&orientation=landscape',
    creatorName: 'أحمد الشمري',
    creatorAvatar: 'https://readdy.ai/api/search-image?query=professional%20Saudi%20man%20portrait%20clean%20background%20warm%20lighting&width=100&height=100&seq=101&orientation=squarish',
    creatorUsername: 'ahmed_shamri',
    donorsCount: 248,
    daysLeft: 18,
    isVerified: true,
    tags: ['تعليم', 'أطفال', 'تقنية'],
    location: 'الرياض',
    updates: [
      {
        id: 'u1-3',
        title: 'وصلنا 75% من الهدف!',
        body: 'بفضل دعمكم الرائع تجاوزنا 37,500 ريال من أصل 50,000 ريال. الفريق يعمل بكامل طاقته على تطوير الوحدات التعليمية الجديدة. نتوقع إطلاق النسخة التجريبية خلال أسبوعين.',
        date: 'منذ 3 أيام',
        time: '10:30 ص',
        type: 'milestone',
        image: 'https://readdy.ai/api/search-image?query=team%20working%20on%20educational%20app%20development%20laptop%20coding%20progress%20milestone%20celebration%20Saudi%20Arabia%20office&width=800&height=400&seq=701&orientation=landscape',
      },
      {
        id: 'u1-2',
        title: 'انتهينا من تصميم 50 درساً تفاعلياً',
        body: 'أعلن بسعادة أننا أنجزنا تصميم 50 درساً تفاعلياً في مادتي الرياضيات والعلوم. كل درس مصمم بعناية ليناسب أطفال المرحلة الابتدائية مع مراعاة القيم الإسلامية. شكراً لكل داعم جعل هذا ممكناً.',
        date: 'منذ أسبوع',
        time: '2:15 م',
        type: 'progress',
        image: 'https://readdy.ai/api/search-image?query=educational%20app%20interface%20Arabic%20children%20colorful%20interactive%20lessons%20design%20progress%20development&width=800&height=400&seq=702&orientation=landscape',
      },
      {
        id: 'u1-1',
        title: 'انطلاق الحملة رسمياً',
        body: 'يسعدني الإعلان عن انطلاق حملة "تطبيق تعليمي للأطفال" رسمياً على منصة هِبة. هذا المشروع يمثل حلماً طويلاً أسعى لتحقيقه لخدمة أطفالنا. أدعوكم للمشاركة والدعم ونشر الحملة.',
        date: 'منذ أسبوعين',
        time: '9:00 ص',
        type: 'news',
      },
    ],
    impactReport: {
      publishedAt: '2024-03-15',
      summary: 'بفضل دعمكم الكريم، أطلقنا التطبيق التعليمي بنجاح وأصبح متاحاً لأكثر من 3,200 طفل في 12 مدرسة ابتدائية بمنطقة الرياض. التطبيق يغطي الآن مادتي الرياضيات والعلوم للصفوف من الأول حتى الرابع الابتدائي.',
      thankYouMessage: 'من أعماق قلبي، أشكر كل داعم كريم آمن بهذا الحلم. تبرعكم لم يكن مجرد أرقام - كان وقوداً حقيقياً غيّر مسار مئات الأطفال. كل طفل يفتح التطبيق ويبتسم هو شهادة على كرمكم وثقتكم. شكراً لأنكم جعلتم هذا ممكناً.',
      expenses: [
        { label: 'تطوير التطبيق', amount: 20000, percentage: 62, icon: 'ri-code-s-slash-line' },
        { label: 'تصميم المحتوى التعليمي', amount: 7000, percentage: 21, icon: 'ri-palette-line' },
        { label: 'الاختبار والجودة', amount: 3500, percentage: 11, icon: 'ri-bug-line' },
        { label: 'التسويق والإطلاق', amount: 2000, percentage: 6, icon: 'ri-megaphone-line' },
      ],
      outcomes: [
        'إطلاق التطبيق على متجري App Store و Google Play',
        'تغطية 4 مواد دراسية بأكثر من 200 درس تفاعلي',
        'تحقيق تقييم 4.8 نجمة من أولياء الأمور',
        'شراكة مع 12 مدرسة ابتدائية في الرياض',
        'تسجيل 3,200 طالب خلال الشهر الأول',
        'تقليل وقت الشرح بنسبة 40% وفق تقارير المعلمين',
      ],
      media: [
        { type: 'image', url: 'https://readdy.ai/api/search-image?query=children%20using%20educational%20tablet%20app%20classroom%20Saudi%20Arabia%20happy%20learning%20colorful%20interface&width=800&height=500&seq=301&orientation=landscape', caption: 'أطفال يستخدمون التطبيق في الفصل الدراسي' },
        { type: 'image', url: 'https://readdy.ai/api/search-image?query=mobile%20app%20educational%20interface%20Arabic%20children%20math%20science%20colorful%20design&width=800&height=500&seq=302&orientation=landscape', caption: 'واجهة التطبيق التعليمي التفاعلية' },
        { type: 'image', url: 'https://readdy.ai/api/search-image?query=school%20launch%20event%20Saudi%20Arabia%20children%20parents%20celebration%20educational%20technology&width=800&height=500&seq=303&orientation=landscape', caption: 'حفل إطلاق التطبيق في المدارس' },
      ],
      beneficiariesCount: 3200,
      completionRate: 100,
    },
    endorsements: [
      {
        id: 'e1',
        name: 'د. خالد المنصور',
        title: 'أستاذ تقنيات التعليم — جامعة الملك عبدالعزيز',
        specialty: 'academic',
        avatar: 'https://i.pravatar.cc/100?img=11',
        quote: 'اطّلعت على المشروع بعناية وأرى فيه نموذجاً واعداً يعالج فجوة حقيقية في التعليم الرقمي. الفريق جاد والمنهجية سليمة.',
        date: 'أبريل 2026',
      },
      {
        id: 'e2',
        name: 'أ. نورة الزهراني',
        title: 'رائدة أعمال — مؤسسة تقنية ناشئة',
        specialty: 'entrepreneur',
        avatar: 'https://i.pravatar.cc/100?img=47',
        quote: 'رأيت كثيراً من مشاريع التعليم الرقمي، وهذا المشروع يتميز بفهمه العميق لاحتياجات الطالب السعودي. أزكّيه بكل ثقة.',
        date: 'مارس 2026',
      },
    ],
  },
  {
    id: '2',
    title: 'مشروع مقهى الكتاب - ثقافة وقهوة',
    description: 'حلمي إنشاء مقهى ثقافي يجمع بين القهوة العربية الأصيلة والكتب، مكان يلتقي فيه المثقفون والمبدعون في بيئة هادئة وملهمة بقلب الرياض.',
    category: 'مشاريع',
    type: 'personal_support',
    goalAmount: 80000,
    raisedAmount: 41200,
    coverImage: 'https://readdy.ai/api/search-image?query=cozy%20Arabic%20coffee%20shop%20bookstore%20warm%20lighting%20books%20shelves%20elegant%20interior%20Saudi%20Arabia&width=800&height=500&seq=3&orientation=landscape',
    creatorName: 'سارة القحطاني',
    creatorAvatar: 'https://readdy.ai/api/search-image?query=professional%20Saudi%20woman%20portrait%20clean%20background%20warm%20lighting%20hijab&width=100&height=100&seq=103&orientation=squarish',
    creatorUsername: 'sara_qahtani',
    donorsCount: 312,
    daysLeft: 25,
    isVerified: true,
    tags: ['مشاريع', 'ثقافة', 'قهوة'],
    location: 'الرياض',
    updates: [
      {
        id: 'u2-3',
        title: 'تم توقيع عقد الإيجار!',
        body: 'خبر رائع! وقّعنا عقد إيجار المكان في حي العليا بالرياض. المساحة 180 متر مربع في موقع مميز. بدأنا أعمال التصميم الداخلي وتوقعنا الافتتاح خلال 6 أسابيع.',
        date: 'منذ 5 أيام',
        time: '11:00 ص',
        type: 'milestone',
        image: 'https://readdy.ai/api/search-image?query=coffee%20shop%20interior%20design%20renovation%20construction%20warm%20lighting%20elegant%20Arabic%20bookstore%20Saudi%20Arabia%20progress&width=800&height=400&seq=703&orientation=landscape',
      },
      {
        id: 'u2-2',
        title: 'شكراً لأول 200 داعم',
        body: 'وصلنا لـ 200 داعم كريم في أقل من أسبوعين! هذا الدعم يعني لي الكثير ويؤكد أن الفكرة تلامس قلوبكم. كل تبرع يقربنا من تحقيق الحلم. شكراً من أعماق قلبي.',
        date: 'منذ أسبوع',
        time: '8:45 م',
        type: 'thanks',
      },
      {
        id: 'u2-1',
        title: 'انطلاق حملة مقهى الكتاب',
        body: 'أطلق اليوم حملة "مقهى الكتاب" على منصة هِبة. حلمت بهذا المكان منذ سنوات — مساحة تجمع القهوة العربية الأصيلة والكتب في بيئة هادئة وملهمة. أدعوكم لتكونوا شركاء في هذا الحلم.',
        date: 'منذ أسبوعين',
        time: '10:00 ص',
        type: 'news',
        image: 'https://readdy.ai/api/search-image?query=coffee%20shop%20concept%20design%20mood%20board%20books%20warm%20lighting%20elegant%20Arabic%20interior%20Saudi%20Arabia%20vision&width=800&height=400&seq=704&orientation=landscape',
      },
    ],
    impactReport: {
      publishedAt: '2024-04-02',
      summary: 'افتتحنا مقهى الكتاب رسمياً في حي العليا بالرياض! المكان أصبح وجهة ثقافية حقيقية يرتادها المثقفون والمبدعون. خلال الشهر الأول استقبلنا أكثر من 2,800 زائر وأقمنا 6 فعاليات ثقافية.',
      thankYouMessage: 'لو لم تكونوا موجودين، لكان هذا المكان مجرد حلم في دفتر ملاحظاتي. كل كوب قهوة يُقدَّم هنا، وكل كتاب يُقرأ في هذا الفضاء، هو ثمرة ثقتكم وكرمكم. أنتم شركاء حقيقيون في هذا المشروع، وبابنا مفتوح لكم دائماً.',
      expenses: [
        { label: 'تجهيز المكان والديكور', amount: 32000, percentage: 40, icon: 'ri-store-2-line' },
        { label: 'المعدات والأجهزة', amount: 24000, percentage: 30, icon: 'ri-tools-line' },
        { label: 'المخزون الأولي', amount: 16000, percentage: 20, icon: 'ri-shopping-bag-line' },
        { label: 'التسويق والإطلاق', amount: 8000, percentage: 10, icon: 'ri-megaphone-line' },
      ],
      outcomes: [
        'افتتاح المقهى في موعده المحدد بحي العليا',
        'استقبال 2,800 زائر خلال الشهر الأول',
        'إقامة 6 فعاليات ثقافية وأمسيات أدبية',
        'توفير 4 فرص عمل للشباب السعودي',
        'مكتبة تضم أكثر من 500 كتاب للقراءة المجانية',
        'تقييم 4.9 نجمة على Google Maps',
      ],
      media: [
        { type: 'image', url: 'https://readdy.ai/api/search-image?query=cozy%20Arabic%20bookstore%20coffee%20shop%20interior%20warm%20lighting%20books%20shelves%20elegant%20Saudi%20Arabia%20opening%20day&width=800&height=500&seq=304&orientation=landscape', caption: 'المقهى في يوم الافتتاح الرسمي' },
        { type: 'image', url: 'https://readdy.ai/api/search-image?query=cultural%20event%20literary%20evening%20Arabic%20coffee%20shop%20people%20gathering%20books%20warm%20atmosphere&width=800&height=500&seq=305&orientation=landscape', caption: 'أمسية أدبية في مقهى الكتاب' },
        { type: 'image', url: 'https://readdy.ai/api/search-image?query=Arabic%20coffee%20specialty%20drinks%20latte%20art%20beautiful%20presentation%20cafe%20warm%20tones&width=800&height=500&seq=306&orientation=landscape', caption: 'تشكيلة القهوة المميزة' },
      ],
      beneficiariesCount: 2800,
      completionRate: 100,
    },
  },
  {
    id: '3',
    title: 'بودكاست "حكايا عربية" - الموسم الثاني',
    description: 'بودكاست يروي قصص نجاح رواد الأعمال العرب ويلهم الشباب. نحتاج دعمكم لإنتاج الموسم الثاني بجودة أعلى وضيوف أكثر تأثيراً.',
    category: 'إبداع',
    type: 'personal_support',
    goalAmount: 30000,
    raisedAmount: 22800,
    coverImage: 'https://readdy.ai/api/search-image?query=Arabic%20podcast%20studio%20microphone%20professional%20recording%20creative%20content%20creator%20warm%20modern&width=800&height=500&seq=5&orientation=landscape',
    creatorName: 'محمد العتيبي',
    creatorAvatar: 'https://readdy.ai/api/search-image?query=young%20Saudi%20man%20content%20creator%20portrait%20clean%20background%20casual&width=100&height=100&seq=105&orientation=squarish',
    creatorUsername: 'mohammed_otaibi',
    donorsCount: 567,
    daysLeft: 30,
    isVerified: true,
    tags: ['إبداع', 'بودكاست', 'محتوى'],
    location: 'الدمام',
  },
  {
    id: '4',
    title: 'ورش فن الخط العربي للشباب',
    description: 'مبادرة لتعليم فن الخط العربي الأصيل للشباب السعودي، نريد توفير المعدات والمواد اللازمة لإقامة 20 ورشة عمل مجانية في مختلف مناطق المملكة.',
    category: 'فنون',
    type: 'personal_support',
    goalAmount: 25000,
    raisedAmount: 18750,
    coverImage: 'https://readdy.ai/api/search-image?query=Arabic%20calligraphy%20art%20workshop%20youth%20learning%20traditional%20Islamic%20art%20beautiful%20script%20warm%20tones&width=800&height=500&seq=7&orientation=landscape',
    creatorName: 'نورة الزهراني',
    creatorAvatar: 'https://readdy.ai/api/search-image?query=Saudi%20woman%20artist%20portrait%20clean%20background%20warm%20lighting%20hijab%20creative&width=100&height=100&seq=107&orientation=squarish',
    donorsCount: 189,
    daysLeft: 45,
    isVerified: false,
    tags: ['فنون', 'خط عربي', 'شباب'],
    location: 'مكة المكرمة',
  },
  {
    id: '5',
    title: 'مزرعة عضوية مستدامة في الأحساء',
    description: 'مشروع زراعي مستدام يهدف لإنتاج الخضروات والفواكه العضوية بأساليب حديثة صديقة للبيئة، مع توفير فرص عمل للشباب المحلي.',
    category: 'زراعة',
    type: 'personal_support',
    goalAmount: 95000,
    raisedAmount: 43200,
    coverImage: 'https://readdy.ai/api/search-image?query=organic%20farm%20Saudi%20Arabia%20green%20vegetables%20sustainable%20agriculture%20modern%20greenhouse%20Al-Ahsa&width=800&height=500&seq=8&orientation=landscape',
    creatorName: 'عبدالله الدوسري',
    creatorAvatar: 'https://readdy.ai/api/search-image?query=Saudi%20farmer%20man%20portrait%20clean%20background%20outdoor%20natural%20lighting&width=100&height=100&seq=108&orientation=squarish',
    donorsCount: 421,
    daysLeft: 60,
    isVerified: true,
    tags: ['زراعة', 'بيئة', 'مشاريع'],
    location: 'الأحساء',
  },
  {
    id: '6',
    title: 'قناة يوتيوب للطبخ السعودي الأصيل',
    description: 'أنا شيف شاب من جدة أريد إطلاق قناة يوتيوب متخصصة في الأكلات السعودية الأصيلة والوصفات التراثية. أحتاج دعمكم لشراء معدات التصوير والإضاءة.',
    category: 'إبداع',
    type: 'personal_support',
    goalAmount: 18000,
    raisedAmount: 11400,
    coverImage: 'https://readdy.ai/api/search-image?query=Saudi%20chef%20cooking%20traditional%20food%20kitchen%20YouTube%20content%20creator%20professional%20camera%20setup%20warm&width=800&height=500&seq=9&orientation=landscape',
    creatorName: 'فيصل الغامدي',
    creatorAvatar: 'https://readdy.ai/api/search-image?query=young%20Saudi%20chef%20man%20portrait%20clean%20background%20kitchen%20apron%20warm%20smile&width=100&height=100&seq=109&orientation=squarish',
    donorsCount: 203,
    daysLeft: 22,
    isVerified: true,
    tags: ['طبخ', 'يوتيوب', 'محتوى'],
    location: 'جدة',
  },
  {
    id: '7',
    title: 'ألبوم موسيقى عربية أصيلة',
    description: 'موسيقي سعودي شاب يسعى لتسجيل أول ألبوم موسيقي يجمع بين الآلات التراثية العربية والأساليب الحديثة. الألبوم سيكون متاحاً مجاناً للجميع.',
    category: 'فنون',
    type: 'personal_support',
    goalAmount: 40000,
    raisedAmount: 27600,
    coverImage: 'https://readdy.ai/api/search-image?query=Arabic%20musician%20oud%20instrument%20recording%20studio%20traditional%20music%20modern%20Saudi%20Arabia%20warm%20artistic&width=800&height=500&seq=10&orientation=landscape',
    creatorName: 'تركي المالكي',
    creatorAvatar: 'https://readdy.ai/api/search-image?query=young%20Saudi%20musician%20man%20portrait%20clean%20background%20artistic%20warm&width=100&height=100&seq=110&orientation=squarish',
    donorsCount: 334,
    daysLeft: 35,
    isVerified: true,
    tags: ['موسيقى', 'فنون', 'إبداع'],
    location: 'الرياض',
  },
  {
    id: '8',
    title: 'متجر عبايات تصميم سعودي عصري',
    description: 'مصممة أزياء شابة تريد إطلاق متجرها الخاص للعبايات بتصاميم عصرية تجمع بين الأصالة والحداثة. أحتاج دعمكم لشراء أول دفعة من الأقمشة والمعدات.',
    category: 'مشاريع',
    type: 'personal_support',
    goalAmount: 35000,
    raisedAmount: 19800,
    coverImage: 'https://readdy.ai/api/search-image?query=Saudi%20fashion%20designer%20abaya%20modern%20elegant%20clothing%20boutique%20studio%20warm%20lighting%20creative%20workspace&width=800&height=500&seq=11&orientation=landscape',
    creatorName: 'لمى العنزي',
    creatorAvatar: 'https://readdy.ai/api/search-image?query=Saudi%20woman%20fashion%20designer%20portrait%20clean%20background%20hijab%20elegant%20warm%20smile&width=100&height=100&seq=111&orientation=squarish',
    donorsCount: 276,
    daysLeft: 40,
    isVerified: true,
    tags: ['أزياء', 'تصميم', 'مشاريع'],
    location: 'جدة',
  },
];

export const featuredCampaigns = campaigns.slice(0, 3);

export const categories = [
  { id: 'all', label: 'الكل', icon: 'ri-apps-line' },
  { id: 'education', label: 'تعليم', icon: 'ri-book-open-line' },
  { id: 'projects', label: 'مشاريع', icon: 'ri-briefcase-line' },
  { id: 'creative', label: 'إبداع', icon: 'ri-palette-line' },
  { id: 'arts', label: 'فنون', icon: 'ri-brush-line' },
  { id: 'agriculture', label: 'زراعة', icon: 'ri-plant-line' },
  { id: 'tech', label: 'تقنية', icon: 'ri-code-line' },
  { id: 'food', label: 'طعام', icon: 'ri-restaurant-line' },
];

export const stats = [
  { value: '+2000', label: 'حملة ناجحة' },
  { value: '+50,000', label: 'داعم كريم' },
  { value: '+30M', label: 'ريال جُمعت' },
  { value: '98%', label: 'نسبة الثقة' },
];

export const testimonials = [
  {
    id: '1',
    name: 'خالد المنصور',
    role: 'صانع محتوى',
    avatar: 'https://readdy.ai/api/search-image?query=Saudi%20young%20man%20portrait%20professional%20clean%20background%20warm&width=80&height=80&seq=201&orientation=squarish',
    text: 'منصة هِبة غيّرت حياتي. تمكنت من تمويل مشروعي الإبداعي بدعم من مجتمعي العربي. الأمر أسهل مما تتخيل!',
    rating: 5,
  },
  {
    id: '2',
    name: 'ريم العسيري',
    role: 'مصممة أزياء',
    avatar: 'https://readdy.ai/api/search-image?query=Saudi%20woman%20hijab%20portrait%20professional%20clean%20background%20warm&width=80&height=80&seq=202&orientation=squarish',
    text: 'أطلقت متجري بفضل دعم المجتمع على هِبة. الشفافية والمصداقية هما ما يميز المنصة، داعمونا يثقون بنا.',
    rating: 5,
  },
  {
    id: '3',
    name: 'فيصل الحربي',
    role: 'رائد أعمال',
    avatar: 'https://readdy.ai/api/search-image?query=Saudi%20businessman%20portrait%20professional%20clean%20background%20confident&width=80&height=80&seq=203&orientation=squarish',
    text: 'جمعت تمويل مشروعي في أقل من شهر! الدعم الفني ممتاز والمنصة سهلة الاستخدام جداً.',
    rating: 5,
  },
];
